import React, { useEffect, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fromJS, Map } from 'immutable'
import PropTypes from 'prop-types'
import isNil from 'lodash/isNil'
import _get from 'lodash/get'
import {
  selectUserCanPurchaseGift,
  selectUserCanPurchaseGiftMultipleCurrency,
  selectGiftOrderSuccess,
  selectRecipientEmail,
  selectGiftToken,
  selectGiftPlan,
  selectGiftSku,
} from '../../../services/gift/selectors'
import { GIFT_SUBROUTE_COMPLETE, useNextGiftRoute } from '../../../services/gift/utils/index'
import { GIFT_READY_STATE } from '../../../services/gift/index'
import {
  clearGiftOrderError,
  createGiftOrder,
  setGiftDetails,
} from '../../../services/gift/actions'
import { replaceHistory } from '../../../services/navigation/actions'
import { selectAuthorized, selectAuth } from '../../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../../services/user/selectors'
import { GiftSelection } from '../GiftSelection/index'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'
import { useGetPlanGridData } from '../../../services/plans/hooks'
import { BILLING_PLAN_GRID_LOCATION_GIFT } from '../../../services/plans/utils'
import { GiftLayout } from '../GiftLayout/index'
import GiftForm from '../GiftForm/index'
import GiftFormV2 from '../GiftForm/GiftFormV2'
import { get as getConfig } from '../../../config/index'
import {
  GIFT_REVIEW_PAGE_STATIC_KEY as staticTextStoreKey,
} from './gift-review-page-static-key'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])
const giftMultipleCurrenciesActive = !!_get(config, ['features', 'gift', 'multipleCurrencies'])

const GiftReviewPagePropTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
}

/**
 * @type {React.FC<PropTypes.InferProps<GiftReviewPagePropTypes>>}
 * @returns {React.ReactElement}
 * */
export function GiftReviewPage () {
  const auth = useSelector(selectAuth)
  const authJwt = auth.get('jwt')
  const userLanguage = useSelector(selectPrimaryLanguage)
  const staticText = useSelector(selectStaticTextByKey(staticTextStoreKey))
  const canPurchaseGift = useSelector(
    giftMultipleCurrenciesActive
      ? selectUserCanPurchaseGiftMultipleCurrency
      : selectUserCanPurchaseGift,
  )
  const orderSuccess = useSelector(selectGiftOrderSuccess)
  const recipientEmail = useSelector(selectRecipientEmail)
  const orderDataToken = useSelector(selectGiftToken)
  const sku = useSelector(selectGiftSku)
  let selectedPlan = useSelector(selectGiftPlan)
  const loggedIn = useSelector(selectAuthorized)
  const getNextRoute = useNextGiftRoute()
  const dispatch = useDispatch()
  const formattedPlans = useGetPlanGridData({
    userLanguage,
    authJwt,
    page: BILLING_PLAN_GRID_LOCATION_GIFT,
  })

  if (planGraphQLActive) {
    selectedPlan = formattedPlans.find((gp) => gp.get('sku') === sku) || Map()
  }

  const [sendByEmail, setSendByEmail] = useState(!!recipientEmail)
  const [isEditing, setIsEditing] = useState(false)

  const onToggleEmailFields = useCallback(() => {
    setSendByEmail(!sendByEmail)
  }, [sendByEmail])

  const onToggleEditing = useCallback(() => {
    dispatch(clearGiftOrderError())
    setIsEditing(!isEditing)
  }, [isEditing])

  const pageReady = !isNil(selectedPlan)

  const onValidSubmit = useCallback(
    (model) => {
      const giftData = fromJS(model)
      dispatch(setGiftDetails(giftData))
      setIsEditing(!isEditing)
    },
    [dispatch, isEditing],
  )

  const onCompletePurchase = useCallback(() => {
    dispatch(createGiftOrder())
  }, [dispatch])

  const complete = () => {
    dispatch(createGiftOrder(selectedPlan.get('id')))
  }

  useEffect(() => {
    if (orderSuccess) {
      const nextRoute = getNextRoute({ subRoute: GIFT_SUBROUTE_COMPLETE })
      dispatch(replaceHistory({
        query: { token: orderDataToken },
        scrollToTop: true,
        url: nextRoute,
      }))
    }
  }, [
    replaceHistory,
    orderDataToken,
    getNextRoute,
    orderSuccess,
  ])

  return (
    <GiftLayout
      pageReady={pageReady}
      restricted={canPurchaseGift !== GIFT_READY_STATE.CAN_PURCHASE}
      pageTitle={staticText.get('title')}
      description={staticText.get('description')}
      rightSide={
        <GiftSelection
          canPurchaseGift={canPurchaseGift}
          selectedPlan={selectedPlan}
          showMobileTitle={false}
          planData={formattedPlans}
          changeable
        />
      }
      main={
        giftMultipleCurrenciesActive
          ? <GiftFormV2
            toggleEmailFields={onToggleEmailFields}
            completePurchase={planGraphQLActive ? complete : onCompletePurchase}
            canPurchaseGift={canPurchaseGift}
            toggleEditing={onToggleEditing}
            sendByEmail={sendByEmail}
            submit={onValidSubmit}
            editing={isEditing}
            loggedIn={loggedIn}
            reviewing
          />
          : <GiftForm
            toggleEmailFields={onToggleEmailFields}
            completePurchase={planGraphQLActive ? complete : onCompletePurchase}
            canPurchaseGift={canPurchaseGift}
            toggleEditing={onToggleEditing}
            sendByEmail={sendByEmail}
            submit={onValidSubmit}
            editing={isEditing}
            loggedIn={loggedIn}
            reviewing
          />
      }
    />
  )
}

GiftReviewPage.propTypes = GiftReviewPagePropTypes

export default GiftReviewPage
